import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { toast } from 'sonner';
import { LogIn, AlertCircle } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PartnerLoginPage = () => {
  const { login } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const partnerCode = searchParams.get('pc');
  
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [partnerBranding, setPartnerBranding] = useState(null);
  const [loadingBranding, setLoadingBranding] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPartnerBranding = async () => {
      if (!partnerCode) {
        setError('Partner code is required. Please use the correct login URL.');
        setLoadingBranding(false);
        return;
      }

      try {
        const response = await axios.get(`${API_URL}/api/partner-branding/${partnerCode}`);
        setPartnerBranding(response.data);
        setError(null);
      } catch (err) {
        if (err.response?.status === 404) {
          setError('Invalid partner code. Please contact your administrator.');
        } else {
          setError('Failed to load partner information. Please try again.');
        }
      } finally {
        setLoadingBranding(false);
      }
    };

    fetchPartnerBranding();
  }, [partnerCode]);

  // Set partner favicon and title if available
  useEffect(() => {
    if (partnerBranding?.partner_favicon_url) {
      const fav = partnerBranding.partner_favicon_url;
      const name = partnerBranding.partner_name || 'BondMaster';
      document.title = `${name} | Bond Portfolio Management`;
      const setLink = (rel, href) => {
        let el = document.querySelector(`link[rel="${rel}"]`);
        if (!el) { el = document.createElement('link'); el.setAttribute('rel', rel); document.head.appendChild(el); }
        el.setAttribute('href', href);
      };
      setLink('icon', fav);
      setLink('shortcut icon', fav);
      setLink('apple-touch-icon', fav);
    }
    return () => {
      document.title = 'BondMaster | Bond Portfolio Management Platform';
      const def = 'https://bondmaster.com.in/logoBond.png';
      ['icon', 'shortcut icon', 'apple-touch-icon'].forEach(rel => {
        const el = document.querySelector(`link[rel="${rel}"]`);
        if (el) el.setAttribute('href', def);
      });
    };
  }, [partnerBranding]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username || !password) {
      toast.error('Please enter username and password');
      return;
    }
    
    setIsLoading(true);
    try {
      const user = await login(username, password);
      toast.success(`Welcome back, ${user.entity_name || user.username}!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed. Please check your credentials.');
    } finally {
      setIsLoading(false);
    }
  };

  // Show loading state while fetching branding
  if (loadingBranding) {
    return (
      <div className="login-container" data-testid="partner-login-page">
        <div className="w-full max-w-md px-4 text-center">
          <div className="animate-spin h-8 w-8 border-4 border-[#1B4D3E]/30 border-t-[#1B4D3E] rounded-full mx-auto"></div>
          <p className="text-[#64748b] mt-4">Loading...</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="login-container" data-testid="partner-login-page">
        <div className="w-full max-w-md px-4">
          <Card className="glass-card border-0 shadow-xl">
            <CardContent className="pt-8 pb-8 text-center">
              <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
              <h2 className="text-xl font-heading font-bold text-[#1B4D3E] mb-2">Access Error</h2>
              <p className="text-[#64748b] mb-6">{error}</p>
              <Button 
                onClick={() => navigate('/')}
                className="bg-[#1B4D3E] hover:bg-[#163d32] text-white"
              >
                Go to Main Login
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Determine logo and branding
  const hasCustomLogo = partnerBranding?.partner_logo_url && partnerBranding.partner_logo_url.trim() !== '';
  const logoUrl = hasCustomLogo ? partnerBranding.partner_logo_url : 'https://bondmaster.com.in/logoBond.png';

  return (
    <div className="login-container" data-testid="partner-login-page">
      <div className="w-full max-w-md px-4">
        {/* Logo and Header */}
        <div className="text-center mb-8 animate-fadeIn">
          <img 
            src={logoUrl} 
            alt="Logo" 
            className="h-16 mx-auto mb-4 max-w-[200px] object-contain"
            data-testid="partner-login-logo"
          />
          <p className="text-[#475569] mt-2">
            Your trusted bond investment partner
          </p>
        </div>

        {/* Login Card */}
        <Card className="glass-card border-0 shadow-xl animate-fadeIn" style={{ animationDelay: '0.1s' }}>
          <CardHeader className="space-y-1 pb-4">
            <CardTitle className="text-2xl font-heading text-center text-[#1B4D3E]">
              Sign In
            </CardTitle>
            <CardDescription className="text-center text-[#64748b]">
              Enter your credentials to access your portfolio
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="username" className="text-[#1B4D3E] font-medium">
                  Username
                </Label>
                <Input
                  id="username"
                  type="text"
                  placeholder="Enter your username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="h-11 bg-slate-50 border-slate-200 focus:ring-2 focus:ring-[#1B4D3E]/20"
                  data-testid="partner-login-username-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password" className="text-[#1B4D3E] font-medium">
                  Password
                </Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-11 bg-slate-50 border-slate-200 focus:ring-2 focus:ring-[#1B4D3E]/20"
                  data-testid="partner-login-password-input"
                />
              </div>
              <Button 
                type="submit" 
                className="w-full h-11 bg-[#1B4D3E] hover:bg-[#163d32] text-white font-medium rounded-lg transition-all duration-200"
                disabled={isLoading}
                data-testid="partner-login-submit-btn"
              >
                {isLoading ? (
                  <span className="flex items-center gap-2">
                    <span className="animate-spin h-4 w-4 border-2 border-white/30 border-t-white rounded-full"></span>
                    Signing in...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <LogIn className="h-4 w-4" />
                    Sign In
                  </span>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Footer */}
        <p className="text-center text-xs text-[#94a3b8] mt-8">
          © 2025. All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default PartnerLoginPage;
